import { noop } from 'lodash/fp';
import React, { useMemo } from 'react';
import Modal from 'react-modal';

import { Document } from '../icons';
import { LoadingDots } from '../loading-dots/LoadingDots';
import { ConfirmationModal } from './ConfirmationModal';
import styles from './Modal.module.scss';
import { ModalHeader } from './ModalHeader';
import { UploadModalBody } from './UploadModalBody';

interface UploadModalProps {
    isOpen: boolean;
    disabled?: boolean;
    setUploadDocuments: (files: File[]) => void;
    testId?: string;
    modalLabel: string;
    files: File[] | null;
    completeWithAI?: boolean;
    analyseLater?: boolean;
    setAnalyseLater?: (val: boolean) => void;
    setCompleteWithAI?: (val: boolean) => void;
    isUploading: boolean;
    closeModal: () => void;
    uploadDocument: () => void;
    showCompleteWithAIOption?: boolean;
    showAnalyseLaterOption?: boolean;
    canUpload?: boolean;
    needsCounterpartyPermission?: boolean;
}

export const UploadModal: React.FC<UploadModalProps> = ({
    isOpen,
    disabled = false,
    setUploadDocuments,
    testId,
    modalLabel,
    files,
    setAnalyseLater = noop,
    analyseLater = false,
    completeWithAI = false,
    setCompleteWithAI = noop,
    isUploading,
    closeModal,
    uploadDocument,
    showCompleteWithAIOption = false,
    showAnalyseLaterOption = false,
    canUpload = false,
    needsCounterpartyPermission = false
}) => {

    const confirmDisabled = !files || !canUpload || needsCounterpartyPermission;

    const confirmDisabledTooltip = useMemo(() => {
        let disabledArray = [];
        if (!canUpload || needsCounterpartyPermission) {
            disabledArray.push('You do not have permission to upload documents.');
        }
        if (!files && canUpload && !needsCounterpartyPermission) {
            disabledArray.push('You have not added any documents.');
        }
        return disabledArray;
    }, [files, canUpload, needsCounterpartyPermission]);

    const modalBody = useMemo(() => {
        if (needsCounterpartyPermission) {
            return (
                <div className={styles.counterpartyWarning}>You are not currently part of any Access Groups and therefore cannot upload any documents, please contact your admin.</div>
            );
        } else {
            return (
                <UploadModalBody
                    disabled={disabled}
                    setUploadDocuments={setUploadDocuments}
                    testId={testId}
                    files={files}
                    setAnalyseLater={setAnalyseLater}
                    analyseLater={analyseLater}
                    completeWithAI={completeWithAI}
                    setCompleteWithAI={setCompleteWithAI}
                    showCompleteWithAIOption={showCompleteWithAIOption}
                    showAnalyseLaterOption={showAnalyseLaterOption}
                    canUpload={canUpload}
                />
            );
        }
    }, [needsCounterpartyPermission, analyseLater, canUpload, completeWithAI, disabled, files, setAnalyseLater, setCompleteWithAI, setUploadDocuments, showAnalyseLaterOption, showCompleteWithAIOption, testId]);

    const documentPlural = files && files.length > 1 ? 's' : '';

    if (isUploading) {
        return (
            <Modal
                ariaHideApp={false}
                isOpen
                className={styles.uploadingOverlay}
                style={{ overlay: { display: 'flex', zIndex: 10 } }}
            >
                <div className={styles.spinnerWrapper} data-testid='dataset-instance-extracting-wrapper'>
                    <LoadingDots />
                    <div className={styles.spinnerLabel}>Uploading Document{documentPlural}</div>
                    <div className={styles.spinnerWarning}>Do not refresh or close browser during upload process</div>
                </div>
            </Modal>
        );
    }

    return (
        <ConfirmationModal
            isOpen={isOpen}
            closeModal={closeModal}
            confirmLabel='Upload'
            confirmDisabled={confirmDisabled}
            confirm={uploadDocument}
            testId={testId}
            confirmDisabledTooltip={confirmDisabledTooltip}
            closeOnOverlayClick={false}
        >
            <div className={styles.uploadDocumentWrapper} data-testid={`${testId}-wrapper`}>
                <ModalHeader
                    label={modalLabel}
                    testId={testId}
                    icon={Document}
                />
                {modalBody}
            </div>
        </ConfirmationModal>
    );
};
